import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from './utils';
import { Menu, X, Phone, ShoppingCart, Search, UserCircle, ChevronDown } from 'lucide-react';
import LazyCrisp from '@/components/LazyCrisp';
import { getCartCount } from '@/utils/cart';
import MobileNavItem from '@/components/MobileNavItem';
import { useAuth } from '@/lib/AuthContext';

// Below-the-fold / interaction-only components are lazy-loaded to keep the
// initial main bundle (and main-thread parse time) small for Lighthouse perf.
const RegionalHubsFooter = lazy(() => import('@/components/RegionalHubsFooter'));
const HomeSearch = lazy(() => import('@/components/home/HomeSearch'));

export default function Layout({ children, currentPageName }) {
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [cartCount, setCartCount] = useState(getCartCount());
  const accountLink = user?.role === 'admin' ? '/dashboard' : '/portal';



  useEffect(() => {
    const check = () => setCartCount(getCartCount());
    window.addEventListener('storage', check);
    window.addEventListener('focus', check);
    window.addEventListener('pageshow', check);
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    history.pushState = function(...args) {
      check();
      return originalPushState.apply(this, args);
    };
    history.replaceState = function(...args) {
      check();
      return originalReplaceState.apply(this, args);
    };
    return () => {
      window.removeEventListener('storage', check);
      window.removeEventListener('focus', check);
      window.removeEventListener('pageshow', check);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, []);

  const navLinks = [
    { name: 'Home', page: 'Home' },
    {
      name: 'Video Camera Hire',
      page: 'video-camera-and-accessory-hire',
      children: [
        { name: 'Video Camera Hire Catalogue', page: '/camera-catalogue' },
        { name: 'Camera Packages', page: '/video-camera-package-hire' },
        { name: 'Drone Hire', page: '/drone-hire' },
        { name: 'Accessories & Grip', page: '/video-grip-hire' },
        { name: 'Apple Mac Hire', page: '/apple-hire' },
      ],
    },
    {
      name: 'Video Production Services',
      page: 'video-production-services',
      children: [
        { name: 'Professional Crew', page: '/video-production-services/get-videoed-production-crew-hire' },
        { name: 'Camera Operator', page: '/camera-video-crew-operators/video-camera-operator' },
        { name: 'Teleprompter Crew', page: '/accessory-hire/video-cameras-accessories/teleprompter/teleprompter-operator-with-equipment' },
        { name: 'Media Training & Script Coaching', page: '/accessory-hire/video-cameras-accessories/teleprompter/media-training-and-script-coaching' },
        { name: 'Full Production Services', page: '/full-production-services' },
      ],
    },
    { name: 'Special Offers', page: 'SpecialOffers' },
    {
      name: 'Videoed',
      page: 'about-videoed',
      children: [
        { name: 'About Videoed', page: '/about-videoed' },
        { name: 'Client Reviews', page: '/client-reviews' },
        { name: 'Careers in Videoed', page: '/careers-in-videoed' },
        { name: 'Hire Terms', page: '/hire-terms' },
        { name: 'Production Terms', page: '/production-terms' },
        { name: 'Contact Us', page: '/contact-us' },
      ],
    },
    {
      name: 'Resources',
      id: 'resources-menu',
      children: [
        { name: 'Video Camera Hire Guides', page: '/resources/video-camera-hire' },
        { name: 'Teleprompter/Autocue Guides', page: '/resources/teleprompter' },
        { name: 'Apple Mac Hire Guides', page: '/resources/apple-mac-hire' },
      ],
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Skip to main content — keyboard accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:bg-purple-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:text-sm focus:font-semibold"
      >
        Skip to main content
      </a>
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 sm:gap-3 py-3 sm:py-5">
            {/* Logo */}
            <Link to={createPageUrl('Home')} className="flex items-center flex-shrink-0" aria-label="Videoed — Home">
              <div className="bg-gradient-to-r from-purple-600 via-purple-500 to-blue-600 rounded-md sm:rounded-lg p-1 sm:p-1.5">
                <img 
                  src="https://media.base44.com/images/public/694bbff8dc754a40cf01606a/c3383b0ce_videoed_logo.png" 
                  alt="Videoed" 
                  className="h-5 sm:h-7 w-auto"
                  width="140" height="28"
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-6 ml-2">
              {navLinks.map((link) => (
                <React.Fragment key={link.id || link.page}>
                  {link.children ? (
                    <div className="relative group">
                      {link.page ? (
                        <Link
                          to={createPageUrl(link.page)}
                          className={`text-sm font-medium transition-colors inline-flex items-center gap-0.5 ${
                            currentPageName === link.page ? 'text-purple-600' : 'text-gray-700 hover:text-purple-600'
                          }`}
                        >
                          {link.name}
                          <ChevronDown className="w-3.5 h-3.5 opacity-50" />
                          <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-purple-600 transition-transform ${
                            currentPageName === link.page ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                          }`}></span>
                        </Link>
                      ) : (
                        <span className="text-sm font-medium text-gray-700 inline-flex items-center gap-0.5 cursor-default">
                          {link.name}
                          <ChevronDown className="w-3.5 h-3.5 opacity-50" />
                        </span>
                      )}
                      <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl border border-gray-200 shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 py-2">
                        {link.children.map((child) => (
                          <Link
                            key={child.page}
                            to={child.page}
                            className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={createPageUrl(link.page)}
                      className={`text-sm font-medium transition-colors relative group ${
                        currentPageName === link.page ? 'text-purple-600' : 'text-gray-700 hover:text-purple-600'
                      }`}
                    >
                      {link.name}
                      <span className={`absolute -bottom-1 left-0 w-full h-0.5 bg-purple-600 transition-transform ${
                        currentPageName === link.page ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                      }`}></span>
                    </Link>
                  )}
                </React.Fragment>
              ))}
            </nav>

            {/* Right-side icons group */}
            <div className="flex items-center gap-1 sm:gap-3 ml-auto">
              {/* Ranked Badge */}
              <div className="hidden sm:flex items-center gap-1.5 px-2.5 border border-purple-200 rounded-lg bg-purple-50/50 flex-shrink-0" style={{ height: '40px' }}>
                <img 
                  src="https://base44.app/api/apps/694bbff8dc754a40cf01606a/files/mp/public/694bbff8dc754a40cf01606a/36982c556_image.webp"
                  alt="Ranked No1"
                  className="h-6 w-auto"
                  width="24" height="29"
                  decoding="async"
                />
                <span className="text-[10px] font-semibold text-purple-700 tracking-wide leading-tight">Ranked No1</span>
              </div>
              <Link
                to={accountLink}
                className={`p-1.5 sm:p-2 rounded-full transition-colors ${
                  currentPageName === 'dashboard' || currentPageName === 'portal' ? 'text-purple-600 bg-purple-50' : 'text-gray-600 hover:text-purple-600 hover:bg-purple-50'
                }`}
                title={user?.role === 'admin' ? 'Dashboard' : 'My Account'}
              >
                <UserCircle className="w-4 h-4 sm:w-5 sm:h-5" />
              </Link>

              {/* Search Icon */}
              <button
                onClick={() => setSearchOpen(true)}
                className="p-1.5 sm:p-2 text-gray-600 hover:text-purple-600 transition-colors"
                title="Search"
              >
                <Search className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              {/* Cart Icon */}
              <Link
                to="/booking-summary"
                className="relative p-1.5 sm:p-2 text-gray-600 hover:text-purple-600 transition-colors"
                title="View booking cart"
              >
                <ShoppingCart className="w-4 h-4 sm:w-5 sm:h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 sm:w-5 sm:h-5 bg-purple-600 text-white text-[9px] sm:text-[10px] font-bold rounded-full flex items-center justify-center shadow-sm">
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* Phone Number */}
              <a
                href="tel:02080502345"
                className="p-1.5 sm:p-2 text-gray-600 hover:text-purple-600 transition-colors md:hidden"
                title="Call us"
              >
                <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
              </a>
              <div className="hidden md:flex items-center space-x-2 text-sm text-gray-600 hover:text-purple-600 transition-colors">
                <Phone className="w-4 h-4" />
                <span className="font-medium">0208 050 2345</span>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-3 text-gray-700 hover:text-purple-600 transition-colors flex-shrink-0 min-w-[48px] min-h-[48px] flex items-center justify-center"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="lg:hidden py-4 border-t border-gray-200">
              {navLinks.map((link) => (
                <MobileNavItem
                  key={link.page}
                  link={link}
                  currentPageName={currentPageName}
                  createPageUrl={createPageUrl}
                  onClose={() => setMobileMenuOpen(false)}
                />
              ))}
              <Link
                to={accountLink}
                className={`block py-3 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  currentPageName === 'dashboard' || currentPageName === 'portal' ? 'text-purple-600' : 'text-gray-700 hover:text-purple-600'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <UserCircle className="w-4 h-4" />
                {user?.role === 'admin' ? 'Dashboard' : 'My Account'}
              </Link>
              <div className="flex items-center space-x-2 text-sm mt-4 pt-4 border-t border-gray-200 text-gray-600">
                <Phone className="w-4 h-4" />
                <span className="font-medium">0208 050 2345</span>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Search Modal */}
      {searchOpen && (
        <div className="fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSearchOpen(false)} />
          <div className="absolute inset-x-0 top-0 bg-white shadow-2xl">
            <div className="container mx-auto px-4 py-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-gray-500">What are you looking for?</span>
                <button onClick={() => setSearchOpen(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <Suspense fallback={null}>
                <HomeSearch onSelect={() => setSearchOpen(false)} />
              </Suspense>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main id="main-content" className="flex-1">
        {children}
      </main>

      {/* Regional Hubs Footer */}
      <Suspense fallback={null}>
        <RegionalHubsFooter />
      </Suspense>

      {/* Footer */}
      <LazyCrisp />
      <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-gray-300">
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* What We Do */}
            <div>
              <h3 className="text-white font-bold text-lg mb-6">What We Do</h3>
              <ul className="space-y-2">
                <li><Link to={createPageUrl('video-camera-and-accessory-hire')} className="hover:text-white transition-colors">Video Camera Hire</Link></li>
                <li><Link to="/accessory-hire/video-cameras-accessories/teleprompter/teleprompter-operator-with-equipment" className="hover:text-white transition-colors">Teleprompter Operator</Link></li>
                <li><Link to={createPageUrl('video-production-services')} className="hover:text-white transition-colors">Video Production</Link></li>
                <li><Link to="/video-production-services/get-videoed-production-crew-hire" className="hover:text-white transition-colors">Video Camera Crew</Link></li>
                <li><Link to="/accessory-hire/video-cameras-accessories/teleprompter/media-training-and-script-coaching" className="hover:text-white transition-colors">Media Training &amp; Script Coaching</Link></li>
                <li><Link to="/apple-hire" className="hover:text-white transition-colors">Apple Mac Hire</Link></li>
                <li><Link to="/drone-hire" className="hover:text-white transition-colors">Drone Hire</Link></li>
              </ul>
            </div>

            {/* Get Videoed */}
            <div>
              <h3 className="text-white font-bold text-lg mb-6">Get Videoed</h3>
              <ul className="space-y-2">
                <li><Link to="/about-videoed" className="hover:text-white transition-colors">About Videoed</Link></li>
                <li><Link to="/client-reviews" className="hover:text-white transition-colors">Client Reviews</Link></li>
                <li><Link to="/case-studies" className="hover:text-white transition-colors">Case Studies</Link></li>
                <li><Link to="/careers-in-videoed" className="hover:text-white transition-colors">Careers in Videoed</Link></li>
                <li><Link to="/hire-terms" className="hover:text-white transition-colors">Hire Terms</Link></li>
                <li><Link to="/production-terms" className="hover:text-white transition-colors">Production Terms</Link></li>
                <li><Link to="/contact-us" className="hover:text-white transition-colors">Contact Us</Link></li>
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              </ul>
            </div>

            {/* Here to Help */}
            <div>
              <h3 className="text-white font-bold text-lg mb-6">Here to Help</h3>
              <ul className="space-y-2">
                <li><Link to="/contact-us" className="hover:text-white transition-colors">Live Chat</Link></li>
                <li><Link to="/portal" className="hover:text-white transition-colors">My Bookings</Link></li>
                <li><Link to="/charity-support-and-social-enterprise-activity" className="hover:text-white transition-colors">Charity Support</Link></li>
                <li><Link to="/student-and-educational-discount-video-camera-hire-and-production-services" className="hover:text-white transition-colors">Student &amp; Education</Link></li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h3 className="text-white font-bold text-lg mb-6">Resources</h3>
              <ul className="space-y-2">
                <li><Link to="/resources/video-camera-hire" className="hover:text-white transition-colors">Video Camera Hire Guides</Link></li>
                <li><Link to="/resources/teleprompter" className="hover:text-white transition-colors">Teleprompter/Autocue Guides</Link></li>
                <li><Link to="/resources/apple-mac-hire" className="hover:text-white transition-colors">Apple Mac Hire Guides</Link></li>

              </ul>
            </div>

          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/10 mt-12 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm text-gray-400">&copy; {new Date().getFullYear()} Videoed. All rights reserved.</p>
              <p className="text-sm text-gray-400 flex items-center gap-2">
                <img 
                  src="https://base44.app/api/apps/694bbff8dc754a40cf01606a/files/mp/public/694bbff8dc754a40cf01606a/36982c556_image.webp"
                  alt="Ranked"
                  className="h-4 w-auto"
                  width="16" height="20"
                  loading="lazy"
                  decoding="async"
                />
                Independently Ranked No.1 with 6,807 5 star reviews
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}